"use client";

// ─── Pole aktywacji kodu prezentowego ────────────────────────────
// Reużywalny komponent: panel użytkownika, koszyk, checkout.
// Po poprawnym kodzie kurs zostaje przypisany do konta (bez płatności),
// kod oznaczany jest jako wykorzystany. Pełna walidacja i obsługa błędów.

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ticket, Loader2, Check, AlertCircle } from "lucide-react";
import { useToast } from "@/lib/toast";
import { normalizeGiftCodeInput } from "@/lib/gifts-format";

export default function GiftCodeRedeem({
  variant = "card",
  light = false,
  onRedeemed,
}: {
  variant?: "card" | "inline";
  light?: boolean;
  onRedeemed?: (course: { title: string; slug: string }) => void;
}) {
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<{ title: string; slug: string } | null>(null);

  async function handleRedeem(e?: React.FormEvent) {
    e?.preventDefault();
    setError("");
    const normalized = normalizeGiftCodeInput(code);
    if (!normalized) {
      setError("Podaj kod prezentowy");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/gifts/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: normalized }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Nie udało się aktywować kodu");
      }
      setDone(data.course);
      setCode("");
      toast({
        variant: "gift",
        title: data.course.title,
        description: data.donorName
          ? `Kurs aktywowany — prezent od ${data.donorName}`
          : "Kurs został dodany do Twojego konta",
        action: { label: "Przejdź do kursu", href: `/platforma/panel/kurs/${data.course.slug}` },
        duration: 8000,
      });
      onRedeemed?.(data.course);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wystąpił błąd");
    } finally {
      setLoading(false);
    }
  }

  const wrapperClass =
    variant === "card"
      ? light
        ? "rounded-2xl border border-slate-100 bg-white p-5"
        : "rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-700/50 dark:bg-slate-800/80"
      : "";

  return (
    <div className={wrapperClass}>
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-600 ${light ? "" : "dark:bg-violet-500/15 dark:text-violet-400"}`}>
          <Ticket size={16} />
        </span>
        <div>
          <p className={`text-sm font-bold text-slate-800 ${light ? "" : "dark:text-white"}`}>Masz kod prezentowy?</p>
          <p className={`text-xs text-slate-500 ${light ? "" : "dark:text-slate-400"}`}>
            Wpisz kod, aby aktywować kurs na swoim koncie
          </p>
        </div>
      </div>

      <form onSubmit={handleRedeem} className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError("");
            }}
            placeholder="XXXX-XXXX-XXXX"
            aria-label="Kod prezentowy"
            autoComplete="off"
            spellCheck={false}
            className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 font-mono text-sm uppercase tracking-wider text-slate-800 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 ${light ? "" : "dark:border-slate-700 dark:bg-slate-800 dark:text-white"}`}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !code.trim()}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Ticket size={16} />}
          Aktywuj
        </button>
      </form>

      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-600"
          >
            <AlertCircle size={13} /> {error}
          </motion.p>
        )}
        {done && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-600"
          >
            <Check size={13} /> Aktywowano: {done.title}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
